import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function ConflictShortVersion() {
  const navigate = useNavigate();
  const [checkedSteps, setCheckedSteps] = useState(() => {
    const saved = localStorage.getItem('conflictShortChecklist');
    return saved ? JSON.parse(saved) : [];
  });

  const steps = [
    { id: 1, text: 'Notice your jackal show. Enjoy it if you want to.' },
    { id: 2, text: 'Give yourself empathy for any jackal things the other person(s) said or did.' },
    { id: 3, text: 'Translate any jackal thoughts you have of the other person into your feelings and needs.' },
    { id: 4, text: 'Translate any jackal thoughts you have of yourself into your feelings and needs.' },
    { id: 5, text: 'Go through a Self-Empathy OFNR with each of your jackal translations.' },
    { id: 6, text: 'Ask yourself if you still have any enemy images of yourself or the other person.' },
    { id: 7, text: 'If so, translate them into your feelings and needs.' },
    { id: 8, text: 'Repeat step 6 and 7 until all enemy images have been translated.' },
    { id: 9, text: 'Ask yourself what feelings and needs might be alive for the other person.' },
    { id: 10, text: 'When you are ready to engage the other person ask them if they are willing to talk to you about the conflict. If so, when.' },
    { id: 11, text: 'Ask the other person if they would let you know if they hear anything you say as criticism or blame.' },
    { id: 12, text: 'Ask them if they would like to go first or have you go first.' },
    { id: 13, text: 'If you are going to start decide whether you want to lead with empathy or honesty.' },
    { id: 14, text: 'Be aware of slowing the conversation down' },
    { id: 15, text: 'Be aware of speaking in smaller chunks of conversation' },
    { id: 16, text: 'Be aware of reflecting back what you heard the other person say' },
    { id: 17, text: 'Be aware of taking turns' },
    { id: 18, text: 'Be aware of expressing gratitude for the other person sharing their honesty or reflecting back to you' },
    { id: 19, text: 'If you do not remember what someone said, ask them to repeat it.' },
    { id: 20, text: 'If you only remember part of what someone said, repeat that back to them and ask them to say again what you missed.' }
  ];

  const toggleCheck = (id) => {
    const updated = checkedSteps.includes(id) ? checkedSteps.filter(i => i !== id) : [...checkedSteps, id];
    setCheckedSteps(updated);
    localStorage.setItem('conflictShortChecklist', JSON.stringify(updated));
  };

  const progress = Math.round((checkedSteps.length / steps.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Working with Conflict: Short Version</h1>
                <p className="text-slate-300 text-sm">A companion to the Dance Floors process</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 29</span></div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-full px-6 py-2 inline-block mb-4">
              <p className="text-white font-semibold">{checkedSteps.length} of {steps.length} steps completed ({progress}%)</p>
            </div>
          </div>
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-2 mb-8">
            <div className="bg-green-600 h-3 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-6 mb-8">
            <p className="text-amber-100 leading-relaxed text-center italic">
              When you have translated all your enemy images into your feelings and needs, you are ready to talk to the other person about the conflict.
            </p>
          </div>

          <div className="space-y-4">
            {steps.map((step) => (
              <div key={step.id} className={`bg-slate-800/40 border ${checkedSteps.includes(step.id) ? 'border-green-700/50' : 'border-slate-700/50'} rounded-lg p-6 transition-all`}>
                <div className="flex items-start gap-4">
                  <Checkbox checked={checkedSteps.includes(step.id)} onCheckedChange={() => toggleCheck(step.id)} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <span className="text-purple-400 font-mono text-lg">{step.id}</span>
                      <p className={`text-lg leading-relaxed ${checkedSteps.includes(step.id) ? 'line-through text-slate-400' : 'text-white'}`}>
                        {step.text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {checkedSteps.length === steps.length && (
            <div className="mt-8 bg-green-900/20 border border-green-700/30 rounded-lg p-6 text-center">
              <p className="text-2xl font-bold text-green-200 mb-2">🎉 Ready for Conversation!</p>
              <p className="text-green-100">You've completed all preparatory steps. You're ready to engage with compassion.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
